import { computed, ref, Ref, watch } from 'vue';
import { useInfiniteQuery, useQuery, useQueryClient, useMutation } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

import { anykrowdApi } from '@/api/anykrowdApi';
import useLoading from '@/shared/composables/useLoading';
import useToast from '@/shared/composables/useToast';
import useStorageService from '@/shared/composables/useStorageService';
import { useAuthStore } from '@/stores/auth/authStore';

// Types
// Note: The queue system only has activities (no sub-activities concept).
// Some interface names and API fields still use "sub_activity" terminology for backwards compatibility
// with the backend API, but these represent actual activities.

export interface QueueActivity {
	id: number;
	name: string;
	slug: string;
	description?: string;
	short_description?: string;
	status: 'draft' | 'published' | 'archived';
	image_url?: string | null;
	main_image_url?: string | null;
	category?: string;
	priority_weight: number;
	capacity_per_cycle: number;
	cycle_duration_minutes: number;
	buffer_time_minutes: number;
	queue_type: 'chronological' | 'time_slot';
	advance_booking_hours: number;
	cancel_before_minutes: number;
	max_bookings_per_user: number;
	regular_price?: number;
	regular_currency_id?: number;
	regularCurrency?: {
		id: number;
		name: string;
		prefix: string;
		suffix: string;
		exchange_rate: number;
	};
	priority_price?: number;
	priority_currency_id?: number;
	priorityCurrency?: {
		id: number;
		name: string;
		prefix: string;
		suffix: string;
		exchange_rate: number;
	};
	upsell_price?: number;
	upsell_currency_id?: number;
	upsellCurrency?: {
		id: number;
		name: string;
		prefix: string;
		suffix: string;
		exchange_rate: number;
	};
	location?: string;
	instructions?: string;
	contact_info?: string;
	notifications_enabled: boolean;
	is_active: boolean;
	sub_activities_count: number;
	sub_activities?: QueueSubActivity[];
	created_at: string;
	updated_at: string;
}

// Note: "QueueSubActivity" is a legacy name - these are actual activities, not sub-activities
export interface QueueSubActivity {
	id: number;
	queue_activity_id: number;
	name: string;
	slug: string;
	description?: string;
	short_description?: string;
	status: 'draft' | 'published' | 'archived';
	image_url?: string | null;
	main_image_url?: string | null;
	start_time?: string;
	end_time?: string;
	operating_days: string[];
	is_24_hours: boolean;
	capacity_override?: number;
	cycle_duration_override?: number;
	buffer_time_override?: number;
	location_override?: string;
	display_order: number;
	is_featured: boolean;
	auto_advance_queue: boolean;
	require_staff_confirmation: boolean;
	allow_walk_ins: boolean;
	is_active: boolean;
	queue_activity: QueueActivity;
	current_queue_count: number;
	estimated_wait_time?: number;
	is_within_operating_hours: boolean;
	can_book: boolean;
	user_booking?: QueueSlot;
	created_at: string;
	updated_at: string;
}

export interface QueueSlot {
	id: number;
	queue_sub_activity_id: number; // Note: API field name uses "sub_activity" but refers to actual activity
	user_id: number;
	order_id?: number;
	position_number: number;
	queue_type: 'normal' | 'fastlane';
	status: 'waiting' | 'called' | 'in_progress' | 'completed' | 'no_show' | 'cancelled';
	estimated_call_time?: string;
	actual_call_time?: string;
	completed_at?: string;
	qr_code?: string;
	event_ticket_id?: number;
	metadata?: any;
	repositioned_at?: string;
	original_position_number?: number;
	original_cycle_number?: number;
	original_booked_time_slot?: string;
	repositioned_reason?: string;
	was_repositioned: boolean;
	booked_date?: string;
	booked_time_slot?: string;
	sub_activity: QueueSubActivity; // Note: API field name uses "sub_activity" but refers to actual activity
	queue_activity?: QueueActivity; // May be included directly in API response
	user: {
		id: number;
		full_name: string;
		email: string;
	};
	positions_ahead?: number;
	estimated_wait_time?: string;
	estimated_wait_minutes?: number;
	can_cancel?: boolean;
	fastlane_available?: boolean;
	created_at: string;
	updated_at: string;
}

export interface UnbookedQueueActivity {
	queue_activity_id: number;
	count: number;
	queue_activity: QueueActivity;
	event_tickets: Array<{
		id: number;
		queue_type?: 'normal' | 'fastlane';
	}>;
}

export interface QueueBookingRequest {
	queue_sub_activity_id: number; // Note: API field name uses "sub_activity" but refers to actual activity
	queue_type?: 'normal' | 'fastlane';
	note?: string;
}

export interface QueuePosition {
	position: number;
	total_ahead: number;
	estimated_wait_time?: number;
	current_cycle: number;
	queue_stats: {
		total_waiting: number;
		total_in_progress: number;
		average_wait_time: number;
	};
}

// Init
const { presentLoading } = useLoading();
const { presentToast } = useToast();
const { get, set } = useStorageService();

// API calls
const getQueueActivitiesApiCall = async (search: string, pageParam: number): Promise<any> => {
	pageParam = pageParam !== null ? pageParam : 1;
	const { data } = await anykrowdApi.get<any>(`/queue/categories?search=${search}&page=${pageParam}`, false);

	// Transform the response to flatten meta_data properties
	// Now we're getting categories with queue_activities instead of sub_activities
	const transformedData =
		data.data?.map((category: any) => ({
			...category,
			...category.meta_data,
			// Ensure is_active defaults to true when meta_data is null
			is_active: category.meta_data?.is_active ?? true,
			image_url: category.image_url ?? null,
			sub_activities:
				category.queue_activities?.map((activity: any) => ({
					...activity,
					image_url: activity.main_image ?? null,
					main_image_url: activity.main_image ?? null,
				})) ?? category.queue_activities,
			sub_activities_count: category.queue_activities?.length || 0,
		})) || [];

	// Return pagination structure expected by TanStack Query
	return {
		data: transformedData,
		current_page: data.current_page || 1,
		last_page: data.last_page || 1,
		per_page: data.per_page || transformedData.length,
		total: data.total || transformedData.length,
	};
};

const getQueueActivityApiCall = async (activityId: number): Promise<QueueActivity> => {
	const { data } = await anykrowdApi.get<any>(`/queue/activities/${activityId}`, false);

	// Handle different response structures
	const activityData = data.data || data;

	// Transform the response to map fields correctly
	const transformedActivity = {
		...activityData,
		...activityData.meta_data,
		sub_activities_count: activityData.sub_activities?.length || 0,
		image_url: activityData.main_image ?? activityData.image_url ?? null,
		main_image_url: activityData.main_image ?? null,
		gallery_images: activityData.gallery_images ?? [],
		// Include pricing fields
		regular_price: activityData.regular_price,
		regular_currency_id: activityData.regular_currency_id,
		regularCurrency: activityData.regularCurrency,
		priority_price: activityData.priority_price,
		priority_currency_id: activityData.priority_currency_id,
		priorityCurrency: activityData.priorityCurrency,
		upsell_price: activityData.upsell_price,
		upsell_currency_id: activityData.upsell_currency_id,
		upsellCurrency: activityData.upsellCurrency,
		sub_activities:
			activityData.sub_activities?.map((sub: any) => ({
				...sub,
				image_url: sub.main_image ?? sub.image_url ?? null,
				main_image_url: sub.main_image ?? null,
				gallery_images: sub.gallery_images ?? [],
				// Add missing fields with defaults
				is_active: sub.is_active ?? true,
				is_within_operating_hours: sub.is_within_operating_hours ?? true,
				can_book: sub.can_book ?? true,
				current_queue_count: sub.current_queue_count ?? 0,
				estimated_wait_time: sub.estimated_wait_time ?? 0,
				display_order: sub.display_order ?? sub.order_index ?? 0,
				is_featured: sub.is_featured ?? false,
				operating_days: sub.operating_days ?? ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
				is_24_hours: sub.is_24_hours ?? false,
				auto_advance_queue: sub.auto_advance_queue ?? true,
				require_staff_confirmation: sub.require_staff_confirmation ?? false,
				allow_walk_ins: sub.allow_walk_ins ?? true,
			})) || [],
	};

	console.log('Transformed activity:', transformedActivity);
	return transformedActivity;
};

const getQueueSubActivitiesApiCall = async (categoryId: number): Promise<QueueSubActivity[]> => {
	const { data } = await anykrowdApi.get<{ data: any[] }>(`/queue/categories/${categoryId}/activities`, false);
	return (
		data.data?.map((activity: any) => ({
			...activity,
			image_url: activity.main_image ?? null,
			main_image_url: activity.main_image ?? null,
			// Add missing fields with defaults to pass filtering
			is_active: activity.is_active ?? true,
			is_within_operating_hours: activity.is_within_operating_hours ?? true,
			can_book: activity.can_book ?? true,
			current_queue_count: activity.current_queue_count ?? 0,
			estimated_wait_time: activity.estimated_wait_time ?? 0,
			display_order: activity.display_order ?? activity.order_index ?? 0,
			is_featured: activity.is_featured ?? false,
			operating_days: activity.operating_days ?? ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
			is_24_hours: activity.is_24_hours ?? false,
			auto_advance_queue: activity.auto_advance_queue ?? true,
			require_staff_confirmation: activity.require_staff_confirmation ?? false,
			allow_walk_ins: activity.allow_walk_ins ?? true,
		})) || []
	);
};

const getMyQueuesApiCall = async (): Promise<QueueSlot[]> => {
	const { data } = await anykrowdApi.get<any>('/queue/my-queues', true);
	// API returns { data: [...] } structure, access the nested data array
	const queuesArray = data.data || data;
	// API returns wrapped slots with additional data, merge them
	return queuesArray.map((item: any) => {
		if (item.slot) {
			// Merge the wrapper fields into the slot
			return {
				...item.slot,
				positions_ahead: item.positions_ahead,
				estimated_wait_time: item.estimated_wait_time,
				estimated_wait_minutes: item.estimated_wait_minutes,
				can_cancel: item.can_cancel,
				fastlane_available: item.fastlane_available,
			};
		}
		return item;
	});
};

const getQueuePositionApiCall = async (slotId: number): Promise<QueuePosition> => {
	const { data } = await anykrowdApi.get<QueuePosition>(`/queue/slots/${slotId}/position`, true);
	return data;
};

const bookQueueSlotApiCall = async (request: QueueBookingRequest): Promise<QueueSlot> => {
	const { data } = await anykrowdApi.post<QueueSlot>('/queue/book', request, true);
	return data;
};

const cancelQueueSlotApiCall = async (slotId: number): Promise<void> => {
	await anykrowdApi.delete(`/queue/slots/${slotId}`, true);
};

const upsellQueueSlotApiCall = async (slotId: number): Promise<QueueSlot> => {
	const { data } = await anykrowdApi.post<{ data: QueueSlot }>(`/queue/slots/${slotId}/upsell`, {}, true);
	return data.data || data;
};

const getUnbookedQueueActivitiesApiCall = async (): Promise<UnbookedQueueActivity[]> => {
	const { data } = await anykrowdApi.get<any>('/queue/unbooked-activities', true);
	return data.data || data;
};

const bookEventTicketSlotApiCall = async (eventTicketId: number, request: { target_date: string; target_time_slot?: string; queue_type?: string }): Promise<QueueSlot> => {
	const { data } = await anykrowdApi.post<any>(`/queue/event-tickets/${eventTicketId}/book-slot`, request, true);
	// API returns { data: QueueSlot } structure
	return data.data || data;
};

// Get queue activities with infinite scroll
const getQueueActivities = (search: Ref<string>) => {
	const activities = ref<QueueActivity[]>();

	const {
		data: activitiesData,
		isFetched: isFetchedActivitiesDataRequests,
		isFetching: isFetchingActivitiesDataRequests,
		isLoading: isLoadingActivitiesData,
		refetch: refetchActivitiesDataRequests,
		remove: removeActivitiesDataRequestsFromCache,
		fetchNextPage,
		hasNextPage,
	} = useInfiniteQuery({
		queryKey: ['queueActivities', search],
		queryFn: async ({ pageParam = 1 }) => await getQueueActivitiesApiCall(search.value, pageParam),
		cacheTime: 1000 * 60 * 30, // 30 minutes
		getNextPageParam: (lastPage) => {
			return lastPage.last_page >= lastPage.current_page + 1 ? lastPage.current_page + 1 : undefined;
		},
		retry: 3,
	});

	// Watch activitiesData
	watch(
		activitiesData,
		async () => {
			if (activitiesData.value) {
				const allActivities = [...activitiesData.value.pages.map((page) => page.data).flat()];
				activities.value = allActivities.filter((activity) => activity.status === 'published' && activity.is_active);

				// Update cache
				const key = ['queueActivities', search.value].join('-');
				let keys = await get('cachedDataKeys');
				if (!keys) {
					keys = [key];
				} else {
					if (!keys.includes(key)) {
						keys.push(key);
					}
				}
				await set('cachedDataKeys', keys);
				set(key, activitiesData.value);
			}
		},
		{ immediate: true },
	);

	return {
		activities,
		fetchNextPage,
		hasNextPage,
		isFetchedActivitiesDataRequests,
		isFetchingActivitiesDataRequests,
		isLoadingActivitiesData,
		refetchActivitiesDataRequests,
		removeActivitiesDataRequestsFromCache,
	};
};

// Get single queue activity
const getQueueActivity = (activityId: number) => {
	const activity = ref<QueueActivity>();

	const {
		data: activityData,
		error: activityError,
		isFetched,
		refetch,
		remove,
	} = useQuery({
		queryKey: ['queueActivity', activityId],
		cacheTime: 1000 * 60 * 30, // 30 minutes
		queryFn: () => getQueueActivityApiCall(activityId),
	});

	watch(
		activityData,
		() => {
			if (activityData.value) {
				activity.value = { ...activityData.value };
				// Update cache
				set(`queueActivity-${activityId}`, activity.value);
			}
		},
		{ immediate: true },
	);

	return {
		activity,
		activityError,
		isFetchedActivity: computed(() => isFetched.value),
		refetchActivity: refetch,
		removeActivityFromCache: remove,
	};
};

// Get queue sub-activities
const getQueueSubActivities = (activityId: number) => {
	const subActivities = ref<QueueSubActivity[]>([]);

	const {
		data: subActivitiesData,
		error: subActivitiesError,
		isFetched,
		refetch,
		remove,
	} = useQuery({
		queryKey: ['queueSubActivities', activityId],
		cacheTime: 1000 * 60 * 30, // 30 minutes
		queryFn: () => getQueueSubActivitiesApiCall(activityId),
	});

	watch(
		subActivitiesData,
		() => {
			if (subActivitiesData.value && Array.isArray(subActivitiesData.value)) {
				// Filter active and published sub-activities within operating hours
				subActivities.value = subActivitiesData.value.filter((subActivity) => subActivity.status === 'published' && subActivity.is_active && subActivity.is_within_operating_hours);

				// Sort by display order and featured status
				subActivities.value.sort((a, b) => {
					if (a.is_featured && !b.is_featured) return -1;
					if (!a.is_featured && b.is_featured) return 1;
					return a.display_order - b.display_order;
				});

				// Update cache
				set(`queueSubActivities-${activityId}`, subActivities.value);
			} else {
				// Initialize as empty array if data is not valid
				subActivities.value = [];
			}
		},
		{ immediate: true },
	);

	return {
		subActivities,
		subActivitiesError,
		isFetchedSubActivities: computed(() => isFetched.value),
		refetchSubActivities: refetch,
		removeSubActivitiesFromCache: remove,
	};
};

// Get user's queue slots
const getMyQueues = () => {
	const { isAuthenticated } = useAuthStore();
	const myQueues = ref<QueueSlot[]>([]);

	const {
		data: myQueuesData,
		error: myQueuesError,
		isFetching,
		refetch,
		remove,
	} = useQuery({
		queryKey: ['myQueues'],
		queryFn: () => getMyQueuesApiCall(),
		cacheTime: 1000 * 60 * 10, // 10 minutes
		enabled: isAuthenticated(),
		refetchInterval: 30000, // Refetch every 30 seconds
	});

	watch(
		myQueuesData,
		() => {
			if (myQueuesData.value && Array.isArray(myQueuesData.value)) {
				// Filter active slots only (include 'active' status from API)
				myQueues.value = myQueuesData.value.filter((slot) => ['active', 'waiting', 'called', 'in_progress', 'completed'].includes(slot.status));

				// Sort by position number
				myQueues.value.sort((a, b) => a.position_number - b.position_number);

				// Update cache
				set('myQueues', myQueues.value);
			} else {
				// Initialize as empty array if no data
				myQueues.value = [];
			}
		},
		{ immediate: true },
	);

	return {
		myQueues,
		myQueuesError,
		isFetchingMyQueues: computed(() => isFetching.value),
		refetchMyQueues: refetch,
		removeMyQueuesFromCache: remove,
	};
};

// Get queue position for a slot
const getQueuePosition = (slotId: number) => {
	const position = ref<QueuePosition>();

	const {
		data: positionData,
		error: positionError,
		isFetching,
		refetch,
	} = useQuery({
		queryKey: ['queuePosition', slotId],
		queryFn: () => getQueuePositionApiCall(slotId),
		cacheTime: 1000 * 60 * 5, // 5 minutes
		refetchInterval: 15000, // Refetch every 15 seconds
	});

	watch(
		positionData,
		() => {
			if (positionData.value) {
				position.value = { ...positionData.value };
			}
		},
		{ immediate: true },
	);

	return {
		position,
		positionError,
		isFetchingPosition: computed(() => isFetching.value),
		refetchPosition: refetch,
	};
};

// Main composable
const useQueue = () => {
	const { t } = useI18n();
	const queryClient = useQueryClient();

	// Book queue slot mutation
	const bookQueueSlotMutation = useMutation({
		mutationFn: bookQueueSlotApiCall,
		onSuccess: () => {
			// Invalidate and refetch queue-related queries
			queryClient.invalidateQueries({ queryKey: ['myQueues'] });
			queryClient.invalidateQueries({ queryKey: ['queueSubActivities'] });
		},
		onError: (error: any) => {
			console.error('Failed to book queue slot:', error);
		},
	});

	// Cancel queue slot mutation
	const cancelQueueSlotMutation = useMutation({
		mutationFn: cancelQueueSlotApiCall,
		onSuccess: () => {
			// Invalidate and refetch queue-related queries
			queryClient.invalidateQueries({ queryKey: ['myQueues'] });
			queryClient.invalidateQueries({ queryKey: ['queueSubActivities'] });
			queryClient.invalidateQueries({ queryKey: ['queuePosition'] });
		},
		onError: (error: any) => {
			console.error('Failed to cancel queue slot:', error);
		},
	});

	// Upsell queue slot mutation
	const upsellQueueSlotMutation = useMutation({
		mutationFn: upsellQueueSlotApiCall,
		onSuccess: () => {
			// Invalidate and refetch queue-related queries
			queryClient.invalidateQueries({ queryKey: ['myQueues'] });
			queryClient.invalidateQueries({ queryKey: ['queuePosition'] });
		},
		onError: (error: any) => {
			console.error('Failed to upsell queue slot:', error);
		},
	});

	// Book queue slot
	const bookQueueSlot = async (request: QueueBookingRequest) => {
		if (!navigator.onLine) {
			await presentToast('top', t('general.connection.offline_common_message'), 5000, 'danger');
			return null;
		}

		const loading = await presentLoading(t('queue.booking_slot'));

		try {
			const result = await bookQueueSlotMutation.mutateAsync(request);
			await presentToast('top', t('queue.slot_booked_successfully'), 3000, 'success');
			loading.dismiss();
			return result;
		} catch (error: any) {
			const message = error?.response?.data?.message || t('queue.booking_failed');
			await presentToast('top', message, 5000, 'danger');
			loading.dismiss();
			return null;
		}
	};

	// Cancel queue slot
	const cancelQueueSlot = async (slotId: number) => {
		if (!navigator.onLine) {
			await presentToast('top', t('general.connection.offline_common_message'), 5000, 'danger');
			return false;
		}

		const loading = await presentLoading(t('queue.cancelling_slot'));

		try {
			await cancelQueueSlotMutation.mutateAsync(slotId);
			await presentToast('top', t('queue.slot_cancelled_successfully'), 3000, 'success');
			loading.dismiss();
			return true;
		} catch (error: any) {
			const message = error?.response?.data?.message || t('queue.cancellation_failed');
			await presentToast('top', message, 5000, 'danger');
			loading.dismiss();
			return false;
		}
	};

	// Upsell queue slot to fastpass
	const upsellQueueSlot = async (slotId: number) => {
		if (!navigator.onLine) {
			await presentToast('top', t('general.connection.offline_common_message'), 5000, 'danger');
			return null;
		}

		const loading = await presentLoading(t('queue.upgrading_to_fastpass'));

		try {
			const result = await upsellQueueSlotMutation.mutateAsync(slotId);
			await presentToast('top', t('queue.upgraded_to_fastpass_successfully'), 3000, 'success');
			loading.dismiss();
			return result;
		} catch (error: any) {
			const message = error?.response?.data?.message || t('queue.upgrade_failed');
			await presentToast('top', message, 5000, 'danger');
			loading.dismiss();
			return null;
		}
	};

	// Format individual price and currency values
	const formatPrice = (priceInCents?: number | null, currency?: any): string => {
		if (!priceInCents || !currency) {
			return t('queue.free');
		}

		const price = priceInCents / 100;
		const fiatPrice = price * currency.exchange_rate;

		return `${currency.prefix}${price.toFixed(2)}${currency.suffix}`;
	};

	// Check if user can book a sub-activity
	const canBookSubActivity = (subActivity: QueueSubActivity, userQueues: QueueSlot[] = []): boolean => {
		if (!subActivity.can_book || !subActivity.is_within_operating_hours) {
			return false;
		}

		// Check if user already has a booking for this sub-activity
		const existingBooking = userQueues.find((slot) => slot.queue_sub_activity_id === subActivity.id && ['waiting', 'called', 'in_progress'].includes(slot.status));

		return !existingBooking;
	};

	// Get estimated wait time display
	const formatWaitTime = (minutes?: number): string => {
		if (!minutes || minutes <= 0) {
			return t('queue.no_wait');
		}

		if (minutes < 60) {
			return t('queue.minutes_wait', { minutes });
		}

		const hours = Math.floor(minutes / 60);
		const remainingMinutes = minutes % 60;

		if (remainingMinutes === 0) {
			return t('queue.hours_wait', { hours });
		}

		return t('queue.hours_minutes_wait', { hours, minutes: remainingMinutes });
	};

	// Get unbooked queue activities
	const getUnbookedQueueActivities = () => {
		const { isAuthenticated } = useAuthStore();
		const unbookedActivities = ref<UnbookedQueueActivity[]>([]);

		const {
			data: unbookedActivitiesData,
			error: unbookedActivitiesError,
			isFetching,
			refetch,
			remove,
		} = useQuery({
			queryKey: ['unbookedQueueActivities'],
			queryFn: () => getUnbookedQueueActivitiesApiCall(),
			cacheTime: 1000 * 60 * 10, // 10 minutes
			enabled: isAuthenticated(),
			refetchInterval: 30000, // Refetch every 30 seconds
		});

		watch(
			unbookedActivitiesData,
			() => {
				if (unbookedActivitiesData.value && Array.isArray(unbookedActivitiesData.value)) {
					unbookedActivities.value = unbookedActivitiesData.value;
				} else {
					unbookedActivities.value = [];
				}
			},
			{ immediate: true },
		);

		return {
			unbookedActivities,
			unbookedActivitiesError,
			isFetchingUnbookedActivities: computed(() => isFetching.value),
			refetchUnbookedActivities: refetch,
			removeUnbookedActivitiesFromCache: remove,
		};
	};

	// Book event ticket slot mutation
	const bookEventTicketSlotMutation = useMutation({
		mutationFn: ({ eventTicketId, request }: { eventTicketId: number; request: { target_date: string; target_time_slot?: string; queue_type?: string } }) => bookEventTicketSlotApiCall(eventTicketId, request),
		onSuccess: () => {
			// Invalidate and refetch queue-related queries
			queryClient.invalidateQueries({ queryKey: ['myQueues'] });
			queryClient.invalidateQueries({ queryKey: ['unbookedQueueActivities'] });
			queryClient.invalidateQueries({ queryKey: ['queueSubActivities'] });
		},
		onError: (error: any) => {
			console.error('Failed to book event ticket slot:', error);
		},
	});

	// Book event ticket slot
	const bookEventTicketSlot = async (eventTicketId: number, request: { target_date: string; target_time_slot?: string; queue_type?: string }) => {
		if (!navigator.onLine) {
			await presentToast('top', t('general.connection.offline_common_message'), 5000, 'danger');
			return null;
		}

		const loading = await presentLoading(t('queue.booking_slot'));

		try {
			const result = await bookEventTicketSlotMutation.mutateAsync({ eventTicketId, request });
			await presentToast('top', t('queue.slot_booked_successfully'), 3000, 'success');
			loading.dismiss();
			return result;
		} catch (error: any) {
			const message = error?.response?.data?.message || t('queue.booking_failed');
			await presentToast('top', message, 5000, 'danger');
			loading.dismiss();
			return null;
		}
	};

	return {
		getQueueActivities,
		getQueueActivity,
		getQueueSubActivities,
		getMyQueues,
		getUnbookedQueueActivities,
		getQueuePosition,
		bookQueueSlot,
		bookEventTicketSlot,
		cancelQueueSlot,
		upsellQueueSlot,
		formatPrice,
		canBookSubActivity,
		formatWaitTime,
		isBookingSlot: computed(() => bookQueueSlotMutation.isLoading.value),
		isCancellingSlot: computed(() => cancelQueueSlotMutation.isLoading.value),
		isUpsellingSlot: computed(() => upsellQueueSlotMutation.isLoading.value),
	};
};

export default useQueue;
